import React, { useState } from 'react'
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa'
import { Trash2,Pencil } from 'lucide-react'
import useCurrentTeacher from '../../../../hooks/useCurrentTeacher'

const Languages = () => {
  const { teacher, fetchCurrentTeacher } = useCurrentTeacher()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState('') // for add/edit
  const [originalLanguage, setOriginalLanguage] = useState('') // track original for edit

  if (!teacher) return null

  // Open modal for edit
  const handleEdit = (lang) => {
    setCurrentLanguage(lang)
    setOriginalLanguage(lang)
    setIsModalOpen(true)
  }

  // Open modal for add
  const handleAdd = () => {
    setCurrentLanguage('')
    setOriginalLanguage('')
    setIsModalOpen(true)
  }

  // Delete language
  const handleDelete = async (lang) => {
    if (!window.confirm(`Are you sure you want to delete ${lang}?`)) return

    try {
      const updatedLanguages = teacher.languages.filter(l => l !== lang)
      const formData = new FormData()
      formData.append('languages', JSON.stringify(updatedLanguages))

      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/teacher/${teacher.id}/`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: formData,
      })

      if (res.ok) {
        alert('Language deleted successfully!')
        fetchCurrentTeacher()
      } else {
        const data = await res.json()
        alert(data.msg || 'Failed to delete language')
      }
    } catch (err) {
      console.error(err)
      alert('Error deleting language')
    }
  }

  // Save language (add or update)
  const handleSave = async () => {
    if (!currentLanguage.trim()) {
      alert('Language name cannot be empty')
      return
    }

    try {
      let updatedLanguages
      if (originalLanguage && teacher.languages.includes(originalLanguage)) {
        // Update existing
        updatedLanguages = teacher.languages.map(l =>
          l === originalLanguage ? currentLanguage : l
        )
      } else {
        // Add new
        updatedLanguages = [...teacher.languages, currentLanguage]
      }

      const formData = new FormData()
      formData.append('languages', JSON.stringify(updatedLanguages))

      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/teacher/${teacher.id}/`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: formData,
      })

      if (res.ok) {
        alert('Language saved successfully!')
        fetchCurrentTeacher()
        setIsModalOpen(false)
      } else {
        const data = await res.json()
        alert(data.msg || 'Failed to save language')
      }
    } catch (err) {
      console.error(err)
      alert('Error saving language')
    }
  }

  return (
  <div className="p-2 md:p-6 bg-white rounded-2xl w-full">
  {/* Header */}
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
        <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Your Languages</h2>
        <p className="text-sm text-gray-500 mt-0.5">Manage your language proficiencies</p>
      </div>
    </div>
    <button
      className="w-10 h-10 bg-[#0f6abf] text-white rounded-full flex items-center justify-center hover:bg-[#0a5ca8] transition-colors"
      onClick={handleAdd}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
    </button>
  </div>

  {/* Languages Table */}
  {teacher.languages && teacher.languages.length > 0 ? (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="grid grid-cols-12 bg-gray-50 py-3 px-4 text-sm font-medium text-gray-700">
        <div className="col-span-10">Language</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      <div className="divide-y divide-gray-100">
        {teacher.languages.map((lang, index) => (
          <div key={index} className="grid grid-cols-12 py-4 px-4 hover:bg-gray-50 transition-colors">
            <div className="col-span-10 flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">{lang}</span>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button
                className="w-9 h-9 bg-gray-100 text-gray-700 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
                onClick={() => handleEdit(lang)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                className="w-9 h-9 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200 transition-colors"
                onClick={() => handleDelete(lang)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-700 mb-2">No languages added</h3>
      <p className="text-sm text-gray-500">Add languages you speak or teach</p>
    </div>
  )}

  {/* Modal */}
  {isModalOpen && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {originalLanguage ? 'Edit Language' : 'Add Language'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">Enter the language name</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <input
            type="text"
            placeholder="e.g. English, Spanish, French"
            value={currentLanguage}
            onChange={(e) => setCurrentLanguage(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
          />
        </div>
        
        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
          <div className="flex items-center justify-end gap-3">
            <button
              className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="px-5 py-2.5 bg-[#0f6abf] text-white font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors"
              onClick={handleSave}
            >
              Save Language
            </button>
          </div>
        </div>
      </div>
    </div>
  )}
</div>

  )
}

export default Languages
